import { Header } from '../../Header';
import { Footer } from '../../Footer';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { BlogMasthead } from '../../blog/BlogMasthead';
import { NewsletterSignupForm } from '../../blog/NewsletterSignupForm';
import { PageType } from '../../Router';
import { BookOpen, Users, Rocket, GraduationCap } from 'lucide-react';

interface BlogAboutPageProps {
  onNavigate: (page: PageType) => void;
}

const STAFF = [
  {
    name: 'Nash Thomas',
    role: 'Co-Founder & CEO',
    bio: 'A high school student and tech entrepreneur who built Hustlance to empower the modern workforce through innovative software.',
    image: '/team/nash.jpg',
  },
  {
    name: 'Amudhan Nagarajan',
    role: 'Co-Founder & CTO',
    bio: 'An enthusiastic young innovator turning bold ideas into real-world technology.',
    image: '/team/amudhan.png',
  },
];

export function BlogAboutPage({ onNavigate }: BlogAboutPageProps) {
  const pillars = [
    { icon: BookOpen, title: 'Practical guides', text: 'Freelancing, portfolios, and pricing — written for where students actually are, not where a decade of experience gets you.' },
    { icon: Users, title: 'Real student stories', text: 'First-hand accounts from students who found their first client, launched a side project, or built something worth showing.' },
    { icon: Rocket, title: 'Platform updates', text: "What's new on Hustlance, straight from the team building it." },
    { icon: GraduationCap, title: 'Career & skills', text: 'What businesses actually look for, and how to build toward it while you\'re still in school.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header onNavigate={onNavigate} />
      <div className="pt-14">
        <BlogMasthead tagline="Why we write, who it's for, and what to expect." />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-lg text-gray-700 leading-relaxed mb-10">
            Hustlance Blog exists for the same reason Hustlance exists: most advice about freelancing,
            careers, and building a portfolio is written for people who already have years of experience —
            not for students figuring it out for the first time. This is where we write for the students
            actually doing that work right now.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 mb-14">
            {pillars.map(({ icon: Icon, title, text }) => (
              <div key={title} className="p-6 rounded-xl border border-gray-100 bg-hustlance-background">
                <Icon className="w-6 h-6 text-hustlance-primary mb-3" />
                <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
                <p className="text-sm text-gray-600">{text}</p>
              </div>
            ))}
          </div>

          <h2 className="font-blocky text-2xl sm:text-3xl text-hustlance-primary mb-8">Staff</h2>
          <div className="grid sm:grid-cols-2 gap-8 mb-14">
            {STAFF.map(({ name, role, bio, image }) => (
              <div key={name} className="text-center">
                <div className="relative w-40 h-40 mx-auto mb-4 overflow-hidden rounded-full bg-hustlance-background">
                  <ImageWithFallback src={image} alt={name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-bold text-gray-900">{name}</h3>
                <p className="text-sm text-hustlance-primary font-medium mb-2">{role}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{bio}</p>
              </div>
            ))}
          </div>

          <NewsletterSignupForm />
        </main>
      </div>
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
